/** Session-scoped exec metric overrides (CONF-212, CONF-258).
 *
 *  DELIBERATELY NOT part of FlatConfig/DomainConfiguration: overrides are quote
 *  OUTPUT data, applied on top of the computed metrics AFTER pricing and BOM
 *  (spec §3). CONF-258 moved the override target from system throughput
 *  totals to each node's raw throughput rating: `value`/`baseline` here are
 *  the per-node figure in canonical GB/s, BEFORE write efficiency and before
 *  MLEC derating (see src/metrics/types.ts `ThroughputBasis`). Capacity
 *  overrides are retired outright — a quote saved under the old model is
 *  partitioned and converted by src/domain/legacyMetricOverrides.ts, not
 *  handled here. Mirrors domain/PricingOverrides.ts in shape and immutability. */

import { convertThroughput } from '../utils/configUtilities';
import type { PerNodeKey, ThroughputBasis } from '../metrics/types';

export type MetricSet = 'afa' | 'hybrid';

export interface MetricKeyMeta {
  key: PerNodeKey;
  label: string;
  note: string | null;
  set: MetricSet;
  /** Display metric (systemMetrics.ts key) this override replaces. */
  metric: string;
  /** System total that carries this key's delta. */
  total: string;
}

/** The six per-node throughput keys (CONF-258 spec §2). Labels/notes are exact UI copy. */
export const METRIC_OVERRIDE_KEYS: readonly MetricKeyMeta[] = [
  { key: 'afaReadPerNode', label: 'Flash Throughput per Node (Read)', note: null, set: 'afa', metric: 'afaReadTransferRate', total: 'totalNvmeReadTransferRate' },
  { key: 'afaWritePerNode', label: 'Flash Throughput per Node (Write)', note: '(before write efficiency)', set: 'afa', metric: 'afaWriteTransferRate', total: 'totalNvmeWriteTransferRate' },
  { key: 'hybridHddReadPerNode', label: 'Mixed Fleet HDD Throughput per Node (Read)', note: '(before MLEC)', set: 'hybrid', metric: 'hybridReadTransferRate', total: 'totalJbodReadTransferRate' },
  { key: 'hybridHddWritePerNode', label: 'Mixed Fleet HDD Throughput per Node (Write)', note: '(before write efficiency and MLEC)', set: 'hybrid', metric: 'hybridWriteTransferRate', total: 'totalJbodWriteTransferRate' },
  { key: 'hybridNvmeReadPerNode', label: 'Mixed Fleet NVMe Throughput per Node (Read)', note: null, set: 'hybrid', metric: 'hybridBurstReadTransferRate', total: 'totalNvmeReadTransferRate' },
  { key: 'hybridNvmeWritePerNode', label: 'Mixed Fleet NVMe Throughput per Node (Write)', note: '(before write efficiency)', set: 'hybrid', metric: 'hybridBurstWriteTransferRate', total: 'totalNvmeWriteTransferRate' },
];

const META_BY_KEY: Record<string, MetricKeyMeta> = Object.fromEntries(METRIC_OVERRIDE_KEYS.map((m) => [m.key, m]));

export const metricMeta = (key: string): MetricKeyMeta => {
  const m = META_BY_KEY[key];
  if (!m) throw new Error(`${key} is not an overridable metric`);
  return m;
};

export interface MetricOverride { value: number; baseline: number }
export type MetricOverrides = Record<string, MetricOverride>;

export const emptyMetricOverrides = (): MetricOverrides => ({});
export const hasMetricOverrides = (o: MetricOverrides): boolean => Object.keys(o).length > 0;
export const countMetricChanges = (o: MetricOverrides): number => Object.keys(o).length;

export function withMetricOverride(o: MetricOverrides, key: string, value: number, baseline: number): MetricOverrides {
  metricMeta(key);
  return { ...o, [key]: { value, baseline } };
}
export function clearMetricOverride(o: MetricOverrides, key: string): MetricOverrides {
  const rest = { ...o };
  delete rest[key];
  return rest;
}
export function clearMetricSet(o: MetricOverrides, set: MetricSet): MetricOverrides {
  return Object.fromEntries(Object.entries(o).filter(([k]) => META_BY_KEY[k]?.set !== set));
}

// convertThroughput is the single source of truth for GB/s <-> GiB/s. It
// encapsulates the app's unit ratio and is imported by
// src/metrics/calculators/systemMetrics.ts to build the display-unit metrics
// this module receives, so we must use it to ensure round-trip consistency.

export const toDisplay = (value: number, isPBUnit: boolean): number => convertThroughput(value, isPBUnit);

export const toCanonical = (value: number, isPBUnit: boolean): number =>
  isPBUnit ? value : value / toDisplay(1, false);

/** Keys whose displayed value differs from the computed one: each override's
 *  display metric plus the system total that carries its delta. Never the
 *  per-node key itself — that key never appears in the computed metrics. */
export function affectedMetricKeys(o: MetricOverrides): Set<string> {
  const keys = new Set<string>();
  for (const k of Object.keys(o)) {
    const meta = META_BY_KEY[k];
    if (!meta) continue;
    keys.add(meta.metric);
    keys.add(meta.total);
  }
  return keys;
}

/** Display metrics = computed metrics with each override's per-node rating
 *  (scaled by the live basis multiplier) substituted for its display metric,
 *  and totals delta-carried (total' = total + Σ(metric' − metric)). Identity
 *  when there are no overrides. An override whose key is missing from the
 *  basis (its set is disabled, or the vendor/config no longer has it) is
 *  skipped — the computed value stands. */
export function applyMetricOverrides(
  metrics: Record<string, any>, basis: ThroughputBasis | undefined, o: MetricOverrides, ctx: { isPBUnit: boolean },
): Record<string, any> {
  if (!hasMetricOverrides(o)) return metrics;
  const out: Record<string, any> = { ...metrics };
  const totalDelta: Record<string, number> = {};
  for (const [key, ov] of Object.entries(o)) {
    const meta = META_BY_KEY[key];
    if (!meta) continue;
    const entry = basis?.[meta.key];
    if (!entry) continue;
    const current = Number(metrics[meta.metric] ?? 0);
    const next = toDisplay(entry.multiplier * ov.value, ctx.isPBUnit);
    out[meta.metric] = next;
    if (meta.total in metrics) {
      totalDelta[meta.total] = (totalDelta[meta.total] ?? 0) + (next - current);
    }
  }
  for (const [total, delta] of Object.entries(totalDelta)) {
    out[total] = Number(metrics[total] ?? 0) + delta;
  }
  return out;
}

const EPS = 1e-6;

/** Overrides whose recorded baseline no longer matches the live basis rating
 *  (the exec changed the config underneath — a quarter or node-model change
 *  that moves the raw per-node figure). A node-count/encoding/MLEC change
 *  alone does not move `rating`, so it does not mark an override stale — only
 *  compared against `basis`, never derived from the multiplier. A key missing
 *  from the basis is skipped (not reported stale). */
export function staleMetricKeys(basis: ThroughputBasis | undefined, o: MetricOverrides): Set<string> {
  const stale = new Set<string>();
  for (const [key, ov] of Object.entries(o)) {
    const meta = META_BY_KEY[key];
    if (!meta) continue;
    const entry = basis?.[meta.key];
    if (!entry) continue;
    if (Math.abs(entry.rating - ov.baseline) > EPS) stale.add(key);
  }
  return stale;
}

export interface MetricDelta { key: string; label: string; baseline: number; custom: number }

/** Audit-payload view of the overrides, canonical per-node GB/s. */
export function metricDeltas(o: MetricOverrides): MetricDelta[] {
  return Object.entries(o).map(([key, ov]) => {
    const meta = metricMeta(key);
    return { key, label: meta.label, baseline: ov.baseline, custom: ov.value };
  });
}
